// Lightweight editor-scoped context so node-views can reach workspace/brief ids
// and the resolved reference list without prop-drilling through TipTap.
import { createContext, useContext } from "react";
import type { RenderReference } from "@/lib/briefDocument";

export type BriefEditorContextValue = {
  workspaceId: string | null;
  briefId: string | null;
  references: RenderReference[];
  /** Workspace Media tier cap in bytes; `null` = unlimited / unknown. */
  workspaceMediaCapBytes?: number | null;
  /**
   * Flush any pending debounced autosave immediately, awaiting the underlying
   * `onChange` (which typically calls `saveUserEditRevision`). Node-views like
   * the footnote/endnote source-attach popover use this to make sure attribute
   * changes (e.g. `refId`, `locator`) hit the DB before the parent refreshes.
   */
  flushPendingSave?: () => Promise<void> | void;
  /**
   * Ask the parent page (Composer) to re-pull piece + draft state so newly
   * attached source references and updated draft docs surface in the
   * Citations Preview without a full page refresh.
   */
  notifyChanged?: () => Promise<void> | void;
};

const defaultValue: BriefEditorContextValue = {
  workspaceId: null,
  briefId: null,
  references: [],
  workspaceMediaCapBytes: null,
};

export const BriefEditorContext = createContext<BriefEditorContextValue>(defaultValue);

export const useBriefEditorContext = (): BriefEditorContextValue => useContext(BriefEditorContext);
